import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
    Sparkles, Send, Plus, MessageSquare, User, Bot, ChevronRight,
    AlertTriangle, BarChart3, Building2, Target, CheckSquare, Presentation,
    Zap, Search, Shield
} from 'lucide-react';
import useAIChat from '../hooks/useAIChat';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
    'sparkles': Sparkles, 'alert-triangle': AlertTriangle, 'bar-chart-3': BarChart3,
    'building-2': Building2, 'target': Target, 'check-square': CheckSquare,
    'presentation': Presentation, 'zap': Zap, 'search': Search, 'shield': Shield,
};

const categoryColors = {
    pipeline: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-600', accent: 'bg-blue-100' },
    accounts: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-600', accent: 'bg-amber-100' },
    deals: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-600', accent: 'bg-emerald-100' },
    activities: { bg: 'bg-violet-50', border: 'border-violet-200', text: 'text-violet-600', accent: 'bg-violet-100' },
    coaching: { bg: 'bg-rose-50', border: 'border-rose-200', text: 'text-rose-600', accent: 'bg-rose-100' },
    general: { bg: 'bg-stone-50', border: 'border-stone-200', text: 'text-stone-600', accent: 'bg-stone-100' },
};

const FALLBACK_SUGGESTIONS = [
    "What are my top risk deals right now?",
    "Summarize my pipeline health",
    "Which accounts need attention?",
    "Help me prep for my next meeting",
];

export default function AIAssistant() {
    const { getAuthHeader } = useAuth();
    const {
        conversations, messages, activeConvId, loading, loadingConvs,
        messagesEndRef, loadConversations, loadConversation, startNewConversation, sendMessage,
    } = useAIChat();
    const [input, setInput] = useState('');
    const [skills, setSkills] = useState([]);
    const location = useLocation();
    const prefillApplied = useRef(false);

    useEffect(() => {
        loadConversations();
        loadSkills();
    }, []);

    // Prefill from the command palette's Ask-AI row.
    // Prefill only — never auto-send (design decision, R2 §3.4).
    useEffect(() => {
        if (prefillApplied.current) return;
        const promptFromNav = location.state?.prompt;
        if (typeof promptFromNav === 'string' && promptFromNav.trim()) {
            setInput(promptFromNav);
            prefillApplied.current = true;
        }
    }, [location.state]);

    const loadSkills = async () => {
        try {
            const res = await axios.get(`${API}/ai/skills`, { headers: getAuthHeader() });
            setSkills(res.data);
        } catch {}
    };

    const handleSend = async (text = null) => {
        const messageText = text || input;
        if (!messageText.trim() || loading) return;
        setInput('');
        const response = await sendMessage(messageText);
        if (!response) toast.error('AI response failed');
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const runSkill = (skill) => {
        handleSend(skill.prompt_template);
    };

    // Group skills by category
    const groupedSkills = skills.reduce((acc, skill) => {
        const cat = skill.category || 'general';
        if (!acc[cat]) acc[cat] = [];
        acc[cat].push(skill);
        return acc;
    }, {});

    return (
        // `h-full`, not `h-[calc(100vh-8rem)]`. The arithmetic version guessed at
        // the height of the chrome above it and was wrong whenever a banner
        // rendered — the impersonation and runtime-update banners both push the
        // page down, and the composer went off-screen. `/ai-assistant` is now in
        // the shell's `isFullscreenPage` allowlist, so the parent is a bounded
        // flex column and `h-full` resolves against it.
        //
        // `min-h-0` is load-bearing on the children, not decoration: a flex item
        // defaults to `min-height: auto`, so without it each column grows to its
        // content height and its own `overflow-y-auto` never scrolls.
        <div className="flex h-full min-h-0 gap-4">
            {/* Sidebar - Conversations */}
            <div className="w-64 flex-shrink-0 bg-white rounded-xl border border-stone-200 flex flex-col overflow-hidden">
                <div className="p-4 border-b border-stone-200">
                    <button onClick={startNewConversation} data-testid="new-conversation-btn"
                        className="w-full flex items-center gap-2 px-3 py-2.5 bg-gradient-to-r from-stone-900 to-stone-700 text-white text-sm font-medium rounded-lg hover:from-stone-800 hover:to-stone-800 transition-all shadow-sm">
                        <Plus className="w-4 h-4" /> New Conversation
                    </button>
                </div>
                <div className="flex-1 overflow-y-auto p-2">
                    {loadingConvs ? (
                        <div className="space-y-2">
                            {[1,2,3].map(i => <div key={i} className="h-12 bg-stone-100 rounded-lg animate-pulse" />)}
                        </div>
                    ) : conversations.length === 0 ? (
                        <div className="text-center text-stone-400 text-xs py-6">
                            <MessageSquare className="w-6 h-6 mx-auto mb-2 opacity-50" />
                            No conversations yet
                        </div>
                    ) : (
                        conversations.map(conv => (
                            <button key={conv.conversation_id} onClick={() => loadConversation(conv.conversation_id)}
                                className={`w-full text-left px-3 py-2.5 rounded-lg text-sm mb-1 transition-colors ${activeConvId === conv.conversation_id ? 'bg-stone-100 text-stone-900' : 'text-stone-600 hover:bg-stone-50'}`}
                                data-testid={`conversation-${conv.conversation_id}`}>
                                <p className="font-medium truncate">{conv.title || 'Conversation'}</p>
                                <p className="text-xs text-stone-400 mt-0.5">
                                    {new Date(conv.updated_at).toLocaleDateString()}
                                </p>
                            </button>
                        ))
                    )}
                </div>
            </div>

            {/* Chat area */}
            <div className="flex-1 bg-white rounded-xl border border-stone-200 flex flex-col overflow-hidden">
                {/* Header */}
                <div className="px-6 py-4 border-b border-stone-200 flex items-center gap-3">
                    <div className="w-9 h-9 ai-gradient rounded-xl flex items-center justify-center">
                        <Sparkles className="w-4 h-4 text-white" />
                    </div>
                    <div>
                        <h2 className="font-semibold text-stone-900 text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>AI Sales Assistant</h2>
                        <p className="text-xs text-stone-400">Powered by GPT-4o — connected to your CRM data</p>
                    </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-6 space-y-4">
                    {messages.length === 0 && !loading ? (
                        <div className="flex flex-col items-center justify-center h-full">
                            <div className="w-16 h-16 ai-gradient rounded-2xl flex items-center justify-center mb-4 ai-glow">
                                <Sparkles className="w-8 h-8 text-white" />
                            </div>
                            <h3 className="text-lg font-semibold text-stone-900 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                Your AI Sales Partner
                            </h3>
                            <p className="text-stone-500 text-sm mb-6 max-w-md text-center">
                                I have access to your full CRM — pipeline, deals, accounts, activities, and contacts. Ask me anything data-driven.
                            </p>

                            {/* Skills Grid */}
                            {skills.length > 0 ? (
                                <div className="w-full max-w-2xl space-y-4">
                                    {Object.entries(groupedSkills).map(([category, catSkills]) => {
                                        const colors = categoryColors[category] || categoryColors.general;
                                        return (
                                            <div key={category}>
                                                <p className="text-xs font-medium text-stone-400 uppercase tracking-wider mb-2 ml-1">{category}</p>
                                                <div className="grid grid-cols-2 gap-2">
                                                    {catSkills.map(skill => {
                                                        const IconComp = iconMap[skill.icon] || Sparkles;
                                                        return (
                                                            <button key={skill.skill_id} onClick={() => runSkill(skill)}
                                                                className={`text-left p-3.5 rounded-xl border ${colors.border} ${colors.bg} hover:shadow-sm transition-all group`}
                                                                data-testid={`skill-btn-${skill.skill_id}`}>
                                                                <div className="flex items-start gap-3">
                                                                    <div className={`w-8 h-8 rounded-lg ${colors.accent} flex items-center justify-center flex-shrink-0`}>
                                                                        <IconComp className={`w-4 h-4 ${colors.text}`} />
                                                                    </div>
                                                                    <div className="min-w-0">
                                                                        <p className="text-sm font-medium text-stone-800 mb-0.5">{skill.name}</p>
                                                                        <p className="text-xs text-stone-500 line-clamp-2">{skill.description}</p>
                                                                    </div>
                                                                    <ChevronRight className="w-4 h-4 text-stone-300 group-hover:text-stone-500 flex-shrink-0 mt-0.5 transition-colors" />
                                                                </div>
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="grid grid-cols-2 gap-2 w-full max-w-lg">
                                    {FALLBACK_SUGGESTIONS.map((s, i) => (
                                        <button key={i} onClick={() => handleSend(s)}
                                            className="text-left p-3 rounded-lg border border-stone-200 hover:border-stone-400 hover:bg-stone-100/30 text-xs text-stone-600 transition-all">
                                            {s}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    ) : (
                        messages.map((msg) => (
                            <div key={msg.message_id} className={`flex items-start gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`} data-testid={`message-${msg.role}`}>
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${msg.role === 'user' ? 'bg-stone-900' : 'ai-gradient'}`}>
                                    {msg.role === 'user' ? <User className="w-4 h-4 text-white" /> : <Sparkles className="w-4 h-4 text-white" />}
                                </div>
                                <div className={`max-w-[75%] px-4 py-3 rounded-2xl text-sm ${msg.role === 'user' ? 'bg-stone-100 text-stone-800 rounded-tr-none' : 'bg-stone-100 text-stone-900 border border-stone-200 rounded-tl-none'}`}>
                                    <p className="whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                                </div>
                            </div>
                        ))
                    )}

                    {/* Loading indicator */}
                    {loading && (
                        <div className="flex items-start gap-3" data-testid="ai-loading">
                            <div className="w-8 h-8 ai-gradient rounded-full flex items-center justify-center flex-shrink-0">
                                <Sparkles className="w-4 h-4 text-white" />
                            </div>
                            <div className="bg-stone-100 border border-stone-200 px-4 py-3 rounded-2xl rounded-tl-none">
                                <div className="flex gap-1">
                                    <div className="w-2 h-2 bg-stone-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                                    <div className="w-2 h-2 bg-stone-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                                    <div className="w-2 h-2 bg-stone-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                                </div>
                            </div>
                        </div>
                    )}
                    <div ref={messagesEndRef} />
                </div>

                {/* Input */}
                <div className="px-6 py-4 border-t border-stone-200">
                    <div className="flex items-end gap-3">
                        <textarea
                            value={input}
                            onChange={e => setInput(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="Ask about your pipeline, deals, accounts, or use a skill above..."
                            rows={1}
                            data-testid="ai-chat-input"
                            style={{ resize: 'none' }}
                            className="flex-1 px-4 py-3 text-sm border border-stone-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-stone-900/20 focus:border-stone-400 transition-all min-h-[48px] max-h-32"
                        />
                        <button onClick={() => handleSend()} disabled={!input.trim() || loading} data-testid="ai-send-btn"
                            className="w-10 h-10 ai-gradient rounded-xl flex items-center justify-center flex-shrink-0 hover:opacity-90 disabled:opacity-40 transition-all shadow-sm">
                            <Send className="w-4 h-4 text-white" />
                        </button>
                    </div>
                    <p className="text-xs text-stone-400 mt-2 text-center">Connected to your CRM data in real-time · Press Enter to send</p>
                </div>
            </div>
        </div>
    );
}
